import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IconSend } from '../ui/icons';
import { cx } from '../ui/cx';

const PLACEHOLDER_RE = /\[[^\]\n]+\]/g;

function nextPlaceholder(text, fromPos) {
  const re = new RegExp(PLACEHOLDER_RE.source, 'g');
  let m;
  let firstMatch = null;
  while ((m = re.exec(text)) !== null) {
    if (firstMatch === null) firstMatch = m;
    if (m.index >= fromPos) return { start: m.index, end: m.index + m[0].length };
  }
  if (firstMatch) return { start: firstMatch.index, end: firstMatch.index + firstMatch[0].length };
  return null;
}

function ChatInput({ onSend, disabled, prefill, isCharter }) {
  const [text, setText] = useState('');
  const textareaRef = useRef(null);

  useEffect(() => {
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = 'auto';
      ta.style.height = Math.min(ta.scrollHeight, 120) + 'px';
    }
  }, [text]);

  useEffect(() => {
    const value = typeof prefill === 'string' ? prefill : prefill?.text;
    if (value == null || value === '') return;
    setText(value);
    setTimeout(() => {
      const ta = textareaRef.current;
      if (!ta) return;
      ta.focus();
      const m = nextPlaceholder(value, 0);
      if (m) ta.setSelectionRange(m.start, m.end);
      else ta.setSelectionRange(value.length, value.length);
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  // Keep the keyboard flow unbroken: the textarea is disabled while the agent replies
  // (which blurs it), so take focus on mount and again whenever it re-enables. Skip when
  // a dialog (e.g. the charter route popup) is open so we never yank focus out of it.
  useEffect(() => {
    if (disabled) return;
    setTimeout(() => {
      const ta = textareaRef.current;
      if (!ta || ta.disabled) return;
      if (document.querySelector('[role="dialog"]')) return;
      ta.focus();
    }, 0);
  }, [disabled]);

  const placeholdersLeft = useMemo(() => {
    const re = new RegExp(PLACEHOLDER_RE.source, 'g');
    return (text.match(re) || []).length;
  }, [text]);

  const handleChange = (e) => {
    setText(e.target.value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setText('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
      return;
    }
    if (e.key === 'Tab') {
      const ta = textareaRef.current;
      if (!ta) return;
      const m = nextPlaceholder(ta.value, ta.selectionEnd);
      if (m) {
        e.preventDefault();
        ta.setSelectionRange(m.start, m.end);
      }
    }
  };

  const hasText = text.trim().length > 0;
  const canSend = hasText && !disabled;
  const hint = hasText
    ? placeholdersLeft > 0
      ? `Tab to next field · ${placeholdersLeft} left`
      : 'Press Enter to send'
    : null;

  return (
    <div className={cx('shrink-0 border-t px-4 py-3', isCharter ? 'border-peoria-border bg-peoria-bg' : 'border-border bg-white')}>
      <form onSubmit={handleSubmit} className="flex items-end gap-2">
        <textarea
          ref={textareaRef}
          placeholder="Type your message..."
          value={text}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          rows={1}
          className={cx(
            'flex-1 w-full resize-none rounded-btn border px-3 py-2 text-sm leading-relaxed',
            'focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed',
            'transition-all duration-150 min-h-[40px] max-h-[120px]',
            isCharter
              ? 'border-peoria-border bg-peoria-surface2 text-peoria-text placeholder:text-peoria-muted focus:ring-peoria-red/40 focus:border-peoria-red'
              : 'border-border bg-white text-body placeholder:text-body/40 focus:ring-primary/30 focus:border-primary'
          )}
        />
        <button
          type="submit"
          disabled={!canSend}
          className={cx(
            'shrink-0 w-10 h-10 rounded-btn flex items-center justify-center cursor-pointer',
            'transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2',
            isCharter ? 'focus:ring-peoria-red/40 focus:ring-offset-peoria-bg' : 'focus:ring-primary/30',
            canSend
              ? (isCharter ? 'bg-peoria-red text-white hover:bg-peoria-red-dark shadow-card' : 'bg-primary text-white hover:bg-primary-dark shadow-card')
              : (isCharter ? 'bg-peoria-surface2 text-peoria-muted cursor-not-allowed' : 'bg-surface text-body/40 cursor-not-allowed')
          )}
          aria-label="Send message"
        >
          <IconSend size={16} />
        </button>
      </form>
      {hint && <div className={cx('text-11 mt-1.5 ml-1', isCharter ? 'text-peoria-muted' : 'text-body/60')}>{hint}</div>}
    </div>
  );
}

export default ChatInput;
